'use client';

import { forwardRef } from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';

import { ButtonProps } from './button.types';
import { styleButton } from './button.styles';

import { Icon } from '@/components/ui/icon.component';
import { Spinner } from '@/components/ui/spinner.component';

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'default',
      color = 'primary',
      disabled,
      startIcon,
      startIconClassName,
      startIconSize = 'lg',
      endIcon,
      endIconClassName,
      children,
      asChild,
      isLoading,
      className,
      asIcon,
      onClick,
      ...props
    },
    ref,
  ) => {
    const Component = asChild ? Slot : 'button';
    const { container, icon } = styleButton({
      variant,
      disabled: disabled || isLoading,
      className,
      color,
      asIcon,
    });

    const ariaLabelText = typeof children === 'string' ? children : 'button';

    const handleClick
      = asChild && !onClick
        ? undefined
        : onClick;

    return (
      <Component
        ref={ref}
        disabled={disabled || isLoading}
        className={container({ className })}
        aria-label={ariaLabelText}
        onClick={handleClick}
        {...props}
      >
        {isLoading
          ? (
              <Spinner />
            )
          : (
              startIcon && (
                <Icon
                  icon={startIcon}
                  size={startIconSize}
                  className={icon({ className: startIconClassName })}
                  data-testid="start-icon"
                />
              )
            )}

        {!asIcon && children && <Slottable>{children}</Slottable>}

        {endIcon && !isLoading && (
          <Icon
            icon={endIcon}
            className={icon({ className: endIconClassName })}
            data-testid="end-icon"
            aria-label="icon button"
          />
        )}
      </Component>
    );
  },
);
Button.displayName = 'Button';
